import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Pagination from '@mui/material/Pagination';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

interface PharmacyStatusSummary {
  ordered: number;
  unordered: number;
  dispensed?: number;
}

interface PharmacyPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalEntries: number;
  pageSize: number;
  statusSummary?: PharmacyStatusSummary; // Optional - only shown for Checked In tab
  rightContent?: React.ReactNode;
  onStatusClick?: (status: 'ordered' | 'unordered' | 'dispensed' | null) => void;
  selectedStatus?: string;
}

/** Status chip component for Ordered/Unordered */
const StatusBadge: React.FC<{
  label: string;
  count: number;
  variant: 'ordered' | 'unordered' | 'dispensed';
  onClick?: () => void;
  isSelected?: boolean;
}> = ({ label, count, variant, onClick, isSelected }) => (
  <Chip
    label={`${label} : ${count.toString().padStart(2, '0')}`}
    size="small"
    onClick={onClick}
    onKeyDown={(e) => {
      if ((e.key === 'Enter' || e.key === ' ') && onClick) {
        e.preventDefault();
        onClick();
      }
    }}
    tabIndex={0}
    role="button"
    aria-pressed={isSelected}
    aria-label={`Filter by ${label.toLowerCase()}, ${count} items${isSelected ? ', currently selected' : ''}`}
    sx={{
      height: 28,
      fontSize: 13,
      fontWeight: 500,
      borderRadius: '14px',
      px: 1,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s ease',
      ...(isSelected
        ? variant === 'ordered'
          ? {
            backgroundColor: '#1E7E34',
            color: '#fff',
            border: '1px solid #1E7E34',
            '&:hover': {
              backgroundColor: '#166028',
            },
          }
          : variant === 'dispensed'
            ? {
              backgroundColor: '#0B5FFF',
              color: '#fff',
              border: '1px solid #0B5FFF',
              '&:hover': { backgroundColor: '#074ad1' },
            }
            : {
              backgroundColor: '#6B7280',
              color: '#fff',
              border: '1px solid #6B7280',
              '&:hover': {
                backgroundColor: '#4B5563',
              },
            }
        : variant === 'ordered'
          ? {
            backgroundColor: '#E6F4EA',
            color: '#1E7E34',
            border: '1px solid #1E7E34',
            '&:hover': {
              backgroundColor: '#D1E7DD',
            },
          }
          : variant === 'dispensed'
            ? {
              backgroundColor: '#E9F0FF',
              color: '#0B5FFF',
              border: '1px solid #0B5FFF',
              '&:hover': { backgroundColor: '#DDE8FF' },
            }
            : {
              backgroundColor: '#fff',
              color: '#6B7280',
              border: '1px solid #D1D5DB',
              '&:hover': {
                backgroundColor: '#F3F4F6',
              },
            }),
    }}
  />
);

const PharmacyPagination: React.FC<PharmacyPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalEntries,
  pageSize,
  statusSummary,
  rightContent,
  onStatusClick,
  selectedStatus,
}) => {
  const startEntry = totalEntries === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, totalEntries);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        py: 2,
        px: 1,
      }}
    >
      {/* Left side: Showing entries + Status badges */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
          Showing {startEntry} to {endEntry} of {totalEntries} entries
        </Typography>
        
        {statusSummary && (
          <Stack direction="row" spacing={1}>
            <StatusBadge 
              label="Ordered" 
              count={statusSummary.ordered} 
              variant="ordered" 
              onClick={() => onStatusClick?.(selectedStatus === 'ordered' ? null : 'ordered')}
              isSelected={selectedStatus === 'ordered'}
            />
            <StatusBadge 
              label="Dispensed" 
              count={statusSummary.dispensed ?? 0} 
              variant="dispensed" 
              onClick={() => onStatusClick?.(selectedStatus === 'dispensed' ? null : 'dispensed')}
              isSelected={selectedStatus === 'dispensed'}
            />
            <StatusBadge 
              label="Unordered" 
              count={statusSummary.unordered} 
              variant="unordered" 
              onClick={() => onStatusClick?.(selectedStatus === 'unordered' ? null : 'unordered')}
              isSelected={selectedStatus === 'unordered'}
            />
          </Stack>
        )}
      </Box>

      {/* Right side: Pagination + optional content */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {rightContent}
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(_, page) => onPageChange(page)}
          shape="rounded"
          size="small"
          sx={{
            '& .MuiPaginationItem-root': {
              fontWeight: 500,
              fontSize: 13,
              minWidth: 28,
              height: 28,
            },
            '& .Mui-selected': {
              backgroundColor: '#232323 !important',
              color: '#fff',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default PharmacyPagination;
